import { useToast } from '@chakra-ui/react';
import { useAddColorMutation, useDeleteColorMutation, useUpdateColorMutation } from 'core/API/ColorsApi';
import { useTranslation } from 'helpers/languages/translations';
import { useRef, useState } from 'react';
import { IColor, IOptionalColor, IUpdateColorRequest } from 'types';
import { TColorsHooks } from '../useColors';

interface IProps {
    colorToSubmit: IOptionalColor;
    setColorToSubmit: TColorsHooks['setColorToSubmit'];
    setIsModalOpen: TColorsHooks['setIsModalOpen'];
    modalType: TColorsHooks['modalType'];
}

const useModal = ({ colorToSubmit, setColorToSubmit, setIsModalOpen, modalType }: IProps) => {
    const [addColor, { isLoading: isAddColorLoading }] = useAddColorMutation();
    const [updateColor, { isLoading: isUpdateColorLoading }] = useUpdateColorMutation();
    const [deleteColor, { isLoading: isDeleteColorLoading }] = useDeleteColorMutation();
    const translate = useTranslation();
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);
    const toast = useToast();

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSubmit = async () => {
        let resp = {};
        try {
            if (modalType === 'add') {
                resp = await addColor({ ...colorToSubmit, object_id: Number(colorToSubmit.object_id) } as IColor);
            } else {
                resp = await updateColor(colorToSubmit as IUpdateColorRequest);
            }
            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('colors.toast.success.update'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }

        handleCloseModal();
    };

    const handleDeleteKeyword = async () => {
        try {
            const resp = await deleteColor({ color_id: colorToSubmit.color_id } as { color_id: number });
            if ('error' in resp) {
                throw new Error();
            } else {
                toast({
                    title: translate('colors.toast.success.delete'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (e) {
            toast({
                title: translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
        handleCloseModal();
    };

    const handleObjectIdChange = (value: number) => {
        setColorToSubmit({ ...colorToSubmit, object_id: value });
    };
    const changeColor = (newColor: string) => {
        setColorToSubmit({ ...colorToSubmit, color: newColor.toUpperCase() });
    };

    return {
        setIsModalOpen,
        addColor,
        isAddColorLoading,
        updateColor,
        isUpdateColorLoading,
        deleteColor,
        isDeleteColorLoading,
        handleCloseModal,
        handleSubmit,
        handleDeleteKeyword,
        updateInitialRef,
        deleteInitialRef,
        handleObjectIdChange,
        changeColor,
    };
};

export default useModal;
export type TModalHooks = ReturnType<typeof useModal>;
